import { useState, type FormEvent } from 'react'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import Add from '@mui/icons-material/Add'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  createUser,
  dominio,
  dominiosAceitos,
  erroDoUsuario,
  fetchDomains,
  fetchUsers,
  PAPEIS,
  setUserActive,
  USERS_CREATE_PERMISSION,
  USERS_QUERY_KEY,
  type IamUser,
} from '../api/iamUsers'
import { fetchMe, fetchTenantBrand, ME_QUERY_KEY } from '../api/session'
import { getWorkTenantId } from '../api/workTenant'
import { ErrorState } from '../components/ErrorState'
import { LoadingState } from '../components/LoadingState'
import { t } from '../i18n'

/**
 * s69 / 2x -- tela de USUARIOS da empresa em que se esta trabalhando (Marcelo 29/10/2026 17:25 e 17:28).
 * - A empresa e SEMPRE a do "Trabalhando em" (ou a propria) e vai EXPLICITA em toda chamada (E11-s69).
 * - Criar pede confirmacao com o NOME da empresa ("Criar andre em TERRASEMEN?").
 * - E-mail fora do dominio da empresa e recusado aqui: o login acha a empresa pelo dominio (a pessoa nao entraria).
 * - Desativar/reativar com confirmacao; nada e apagado. A senha inicial e digitada por quem cria (ADR-0030 D11) e a
 *   pessoa troca no 1o acesso.
 */
const rotulo = (n: string) => PAPEIS.find((p) => p.name === n)?.label ?? n

export function UsersPage() {
  const qc = useQueryClient()
  const me = useQuery({ queryKey: ME_QUERY_KEY, queryFn: fetchMe })
  const tid = getWorkTenantId() ?? me.data?.tenantId ?? null
  const empresa = useQuery({ queryKey: ['tenant-brand', tid], queryFn: () => fetchTenantBrand(tid as string), enabled: !!tid })
  const lista = useQuery({ queryKey: USERS_QUERY_KEY(tid ?? ''), queryFn: () => fetchUsers(tid as string), enabled: !!tid })
  const doms = useQuery({ queryKey: ['tenant-domains', tid], queryFn: () => fetchDomains(tid as string), enabled: !!tid })
  const [novo, setNovo] = useState(false)
  const [mudar, setMudar] = useState<IamUser | null>(null)
  const [erroLinha, setErroLinha] = useState<string | null>(null)
  const ativa = useMutation({
    mutationFn: (u: IamUser) => setUserActive(u.id, !u.isActive),
    onSuccess: () => void qc.invalidateQueries({ queryKey: USERS_QUERY_KEY(tid ?? '') }),
    onError: (e) => setErroLinha(erroDoUsuario(e) === 'exists' ? t.users.selfDenied : t.users.genericError),
  })

  if (me.isPending || (tid && (lista.isPending || empresa.isPending))) return <LoadingState />
  if (lista.isError) return <ErrorState error={lista.error} onRetry={() => void lista.refetch()} />
  const nomeEmpresa = empresa.data?.displayName ?? ''
  const podeCriar = (me.data?.permissions ?? []).includes(USERS_CREATE_PERMISSION)
  const aceitos = dominiosAceitos(doms.data ?? null, me.data?.user?.email ?? '')
  const usuarios = lista.data ?? []

  return (
    <Stack spacing={2}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" useFlexGap spacing={1}>
        <Box>
          <Typography variant="h5" component="h1">
            {t.users.title}
          </Typography>
          <Typography variant="body2" color="text.secondary" data-testid="usuarios-empresa">
            {t.users.ofCompany(nomeEmpresa)}
          </Typography>
        </Box>
        {podeCriar && (
          <Button variant="contained" startIcon={<Add fontSize="small" />} onClick={() => setNovo(true)} data-testid="novo-usuario">
            {t.users.newUser}
          </Button>
        )}
      </Stack>
      {erroLinha && (
        <Alert severity="warning" onClose={() => setErroLinha(null)}>
          {erroLinha}
        </Alert>
      )}
      <Paper variant="outlined">
        {usuarios.length === 0 ? (
          <Typography sx={{ p: 2 }} color="text.secondary" data-testid="usuarios-vazio">
            {t.users.empty}
          </Typography>
        ) : (
          <Table size="small" data-testid="usuarios-lista">
            <TableHead>
              <TableRow>
                <TableCell>{t.users.colName}</TableCell>
                <TableCell>{t.users.colEmail}</TableCell>
                <TableCell>{t.users.colRoles}</TableCell>
                <TableCell>{t.users.colStatus}</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {usuarios.map((u) => (
                <TableRow key={u.id} data-testid={`usuario-${u.username}`}>
                  <TableCell>{u.username}</TableCell>
                  <TableCell>{u.email}</TableCell>
                  <TableCell>{u.roleNames.map(rotulo).join(', ') || t.users.noRole}</TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                      <Chip size="small" label={u.isActive ? t.users.active : t.users.inactive} color={u.isActive ? 'success' : 'default'} />
                      {u.isActive && u.passwordChangeRequired && <Chip size="small" variant="outlined" label={t.users.firstAccessPending} />}
                    </Stack>
                  </TableCell>
                  <TableCell align="right">
                    {podeCriar && (
                      <Button size="small" onClick={() => setMudar(u)} data-testid={`usuario-ativar-${u.username}`}>
                        {u.isActive ? t.users.deactivate : t.users.reactivate}
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>

      <Dialog open={mudar !== null} onClose={() => setMudar(null)} data-testid="usuario-confirmar-situacao">
        <DialogTitle>{mudar?.isActive ? t.users.deactivate : t.users.reactivate}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {mudar ? (mudar.isActive ? t.users.confirmDeactivate(mudar.username, nomeEmpresa) : t.users.confirmReactivate(mudar.username, nomeEmpresa)) : ''}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMudar(null)} autoFocus>
            {t.users.cancel}
          </Button>
          <Button
            variant="contained"
            color={mudar?.isActive ? 'error' : 'primary'}
            data-testid="usuario-confirmar-situacao-sim"
            onClick={() => {
              const u = mudar
              setMudar(null)
              if (u) ativa.mutate(u)
            }}
          >
            {mudar?.isActive ? t.users.deactivate : t.users.reactivate}
          </Button>
        </DialogActions>
      </Dialog>

      {novo && tid && (
        <NovoUsuario
          tenantId={tid}
          empresa={nomeEmpresa}
          aceitos={aceitos}
          existentes={usuarios}
          fechar={() => setNovo(false)}
          criado={() => {
            setNovo(false)
            void qc.invalidateQueries({ queryKey: USERS_QUERY_KEY(tid) })
          }}
        />
      )}
    </Stack>
  )
}

function NovoUsuario({
  tenantId,
  empresa,
  aceitos,
  existentes,
  fechar,
  criado,
}: {
  tenantId: string
  empresa: string
  aceitos: string[]
  existentes: IamUser[]
  fechar: () => void
  criado: () => void
}) {
  const [nome, setNome] = useState('')
  const [email, setEmail] = useState('')
  const [senha, setSenha] = useState('')
  const [senha2, setSenha2] = useState('')
  const [papeis, setPapeis] = useState<string[]>([])
  const [erro, setErro] = useState<string | null>(null)
  const [confirmando, setConfirmando] = useState(false)
  const m = useMutation({
    mutationFn: () =>
      createUser({ tenantId, username: nome.trim().toLowerCase(), email: email.trim().toLowerCase(), password: senha, roleNames: papeis }),
    gcTime: 0,
    onSuccess: () => {
      setSenha('')
      setSenha2('')
      criado()
    },
    onError: (e) => {
      setConfirmando(false)
      const k = erroDoUsuario(e)
      setErro(k === 'exists' ? t.users.errExists : k === 'password' ? t.users.errPassword : k === 'forbidden' ? t.users.errForbidden : t.users.genericError)
    },
  })
  function conferir(e: FormEvent) {
    e.preventDefault()
    const n = nome.trim().toLowerCase()
    const em = email.trim().toLowerCase()
    if (!n) return setErro(t.users.errName)
    if (!dominio(em)) return setErro(t.users.errEmail)
    if (aceitos.length && !aceitos.includes(dominio(em))) return setErro(t.users.errDomain(aceitos.map((d) => '@' + d).join(', ')))
    if (existentes.some((u) => u.username === n || u.email.toLowerCase() === em)) return setErro(t.users.errExists)
    if (senha.length < 12) return setErro(t.users.errPasswordShort)
    if (senha !== senha2) return setErro(t.users.errPasswordMismatch)
    if (papeis.length === 0) return setErro(t.users.errRole)
    setErro(null)
    setConfirmando(true)
  }
  return (
    <Dialog open onClose={fechar} fullWidth maxWidth="sm" data-testid="novo-usuario-dialogo">
      {!confirmando ? (
        <form onSubmit={conferir} data-testid="novo-usuario-form">
          <DialogTitle>{t.users.newUserIn(empresa)}</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ pt: 1 }}>
              <TextField autoFocus label={t.users.fieldName} helperText={t.users.fieldNameHint} value={nome} onChange={(e) => setNome(e.target.value)} slotProps={{ htmlInput: { maxLength: 128 } }} />
              <TextField
                label={t.users.fieldEmail}
                helperText={aceitos.length ? t.users.fieldEmailHint(aceitos.map((d) => '@' + d).join(', ')) : undefined}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                slotProps={{ htmlInput: { maxLength: 255, 'data-testid': 'novo-usuario-email' } }}
              />
              <TextField type="password" label={t.users.fieldPassword} helperText={t.users.fieldPasswordHint} value={senha} onChange={(e) => setSenha(e.target.value)} autoComplete="new-password" slotProps={{ htmlInput: { 'data-testid': 'novo-usuario-senha' } }} />
              <TextField type="password" label={t.users.fieldPassword2} value={senha2} onChange={(e) => setSenha2(e.target.value)} autoComplete="new-password" slotProps={{ htmlInput: { 'data-testid': 'novo-usuario-senha2' } }} />
              <FormGroup>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {t.users.fieldRoles}
                </Typography>
                {PAPEIS.map((p) => (
                  <FormControlLabel
                    key={p.name}
                    label={p.label}
                    control={
                      <Checkbox
                        checked={papeis.includes(p.name)}
                        onChange={(e) => setPapeis((v) => (e.target.checked ? [...v, p.name] : v.filter((x) => x !== p.name)))}
                        slotProps={{ input: { 'aria-label': p.label } }}
                      />
                    }
                  />
                ))}
              </FormGroup>
              {erro && (
                <Alert severity="warning" data-testid="novo-usuario-erro">
                  {erro}
                </Alert>
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={fechar}>{t.users.cancel}</Button>
            <Button type="submit" variant="contained">
              {t.users.next}
            </Button>
          </DialogActions>
        </form>
      ) : (
        <>
          <DialogTitle>{t.users.confirmTitle}</DialogTitle>
          <DialogContent>
            <DialogContentText data-testid="novo-usuario-confirmacao">
              {t.users.confirmCreate(nome.trim().toLowerCase(), empresa, papeis.map(rotulo).join(', '))}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmando(false)}>{t.users.back}</Button>
            <Button variant="contained" disabled={m.isPending} onClick={() => m.mutate()} data-testid="novo-usuario-criar">
              {t.users.create}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  )
}
